import { chatApi } from 'src/pages/chat/slice/chatApi';

import type {
  WhatsAppCallHistoryRow,
  WhatsAppEnableCallingRequest,
  WhatsAppEnableCallingResult,
  WhatsAppRequestCallPermissionRequest,
  WhatsAppRequestCallPermissionResult,
} from '../types/whatsapp-call.types';

const WHATSAPP_CALL_API = {
  initiate: 'api/whatsapp/calls/initiate',
  accept: 'api/whatsapp/calls/accept',
  reject: 'api/whatsapp/calls/reject',
  terminate: 'api/whatsapp/calls/terminate',
  list: 'api/whatsapp/calls/list',
  enable: 'api/whatsapp/calls/enable',
  requestPermission: 'api/whatsapp/calls/request-permission',
  details: (id: string) => `api/whatsapp/calls/${id}`,
};

type ApiEnvelope<T> = {
  data?: T;
  message?: string;
  success?: boolean;
};

export const whatsappCallApi = chatApi.injectEndpoints({
  endpoints: (builder) => ({
    initiateWhatsAppCall: builder.mutation<
      { callId: string },
      { conversationId: string; sdpOffer: string }
    >({
      query: (body) => ({
        url: WHATSAPP_CALL_API.initiate,
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/json' },
      }),
      transformResponse: (response: ApiEnvelope<{ callId: string }>) => ({
        callId: response?.data?.callId ?? '',
      }),
    }),
    acceptWhatsAppCall: builder.mutation<void, { callId: string; sdpAnswer: string }>({
      query: (body) => ({
        url: WHATSAPP_CALL_API.accept,
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/json' },
      }),
    }),
    rejectWhatsAppCall: builder.mutation<void, { callId: string }>({
      query: (body) => ({
        url: WHATSAPP_CALL_API.reject,
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/json' },
      }),
    }),
    terminateWhatsAppCall: builder.mutation<void, { callId: string }>({
      query: (body) => ({
        url: WHATSAPP_CALL_API.terminate,
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/json' },
      }),
    }),
    listWhatsAppCalls: builder.mutation<
      WhatsAppCallHistoryRow[],
      { conversationId?: string; limit?: number }
    >({
      query: (body) => ({
        url: WHATSAPP_CALL_API.list,
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/json' },
      }),
      transformResponse: (response: ApiEnvelope<WhatsAppCallHistoryRow[]>) => response?.data ?? [],
    }),
    enableWhatsAppCalling: builder.mutation<
      WhatsAppEnableCallingResult,
      WhatsAppEnableCallingRequest
    >({
      query: (body) => ({
        url: WHATSAPP_CALL_API.enable,
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/json' },
      }),
      transformResponse: (response: ApiEnvelope<WhatsAppEnableCallingResult>) =>
        response?.data ?? {},
    }),
    requestWhatsAppCallPermission: builder.mutation<
      WhatsAppRequestCallPermissionResult,
      WhatsAppRequestCallPermissionRequest
    >({
      query: (body) => ({
        url: WHATSAPP_CALL_API.requestPermission,
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/json' },
      }),
      transformResponse: (response: ApiEnvelope<WhatsAppRequestCallPermissionResult>) =>
        response?.data ?? {},
    }),
    getWhatsAppCallDetails: builder.query<WhatsAppCallHistoryRow | null, string>({
      query: (id) => ({
        url: WHATSAPP_CALL_API.details(id),
        method: 'GET',
      }),
      transformResponse: (response: ApiEnvelope<WhatsAppCallHistoryRow | null>) =>
        response?.data ?? null,
    }),
  }),
});

export const {
  useInitiateWhatsAppCallMutation,
  useAcceptWhatsAppCallMutation,
  useRejectWhatsAppCallMutation,
  useTerminateWhatsAppCallMutation,
  useListWhatsAppCallsMutation,
  useEnableWhatsAppCallingMutation,
  useRequestWhatsAppCallPermissionMutation,
  useGetWhatsAppCallDetailsQuery,
} = whatsappCallApi;
